import {
  OC_HEAD_COMPARISON_FIELDS,
  toFlagFieldId,
  type OcHeadComparisonFieldId,
} from '@/features/order-confirmation/settings/ocHeadComparisonFields'
import { OC_LINE_COMPARISON_FIELDS } from '@/features/order-confirmation/settings/ocLineComparisonFields'
import type {
  OcLineComparisonFlagFieldId,
  OrderConfirmationSettings,
  UpdateOrderConfirmationSettingsRequest,
} from '@/types/orderConfirmationSettings'

export interface DeviationThresholds {
  delayDays: number
  earlyDays: number
  priceDifferencePercent: number
}

export interface DateBehaviorSettings {
  useHeadDateIfAllRowsAreMissingDates: boolean
  useDeviatingDatesOnRow: boolean
}

export interface ParseFormatSettings {
  floatParsingEnglishFormat: boolean
  dateParsingWeekFormat: boolean
  dateParsingAmericanFormat: boolean
  dateParsingIsoFormat: boolean
  dateParsingDayFirstFormat: boolean
}

export interface LeadTimeSettings {
  useLeadTimeWhenCalculatingArrival: boolean
  leadTimeInDays: number
}

export interface AutomationSettings {
  fullyAutomaticValidation: boolean
}

export interface ErpSyncSettings {
  updateSupplierOrderNumberOnErpSync: boolean
}

export function thresholdsFromSettings(settings: OrderConfirmationSettings): DeviationThresholds {
  return {
    delayDays: settings.days_of_delay_deviation_threshold,
    earlyDays: settings.days_of_early_deviation_threshold,
    priceDifferencePercent: settings.relative_price_difference_deviation_threshold,
  }
}

export function dateBehaviorFromSettings(settings: OrderConfirmationSettings): DateBehaviorSettings {
  return {
    useHeadDateIfAllRowsAreMissingDates: settings.use_head_date_if_all_rows_are_missing_dates,
    useDeviatingDatesOnRow: settings.use_deviating_dates_on_row,
  }
}

export function applyDateBehaviorToSettings(
  base: OrderConfirmationSettings,
  dateBehavior: DateBehaviorSettings,
): OrderConfirmationSettings {
  return {
    ...base,
    use_head_date_if_all_rows_are_missing_dates: dateBehavior.useHeadDateIfAllRowsAreMissingDates,
    use_deviating_dates_on_row: dateBehavior.useDeviatingDatesOnRow,
  }
}

export function parseFormatFromSettings(settings: OrderConfirmationSettings): ParseFormatSettings {
  return {
    floatParsingEnglishFormat: settings.float_parsing_english_format,
    dateParsingWeekFormat: settings.date_parsing_week_format,
    dateParsingAmericanFormat: settings.date_parsing_american_format,
    dateParsingIsoFormat: settings.date_parsing_iso_format,
    dateParsingDayFirstFormat: settings.date_parsing_day_first_format,
  }
}

export function applyParseFormatToSettings(
  base: OrderConfirmationSettings,
  parseFormat: ParseFormatSettings,
): OrderConfirmationSettings {
  return {
    ...base,
    float_parsing_english_format: parseFormat.floatParsingEnglishFormat,
    date_parsing_week_format: parseFormat.dateParsingWeekFormat,
    date_parsing_american_format: parseFormat.dateParsingAmericanFormat,
    date_parsing_iso_format: parseFormat.dateParsingIsoFormat,
    date_parsing_day_first_format: parseFormat.dateParsingDayFirstFormat,
  }
}

export function leadTimeFromSettings(settings: OrderConfirmationSettings): LeadTimeSettings {
  return {
    useLeadTimeWhenCalculatingArrival: settings.use_lead_time_when_calculating_arrival,
    leadTimeInDays: settings.lead_time_in_days,
  }
}

export function applyLeadTimeToSettings(
  base: OrderConfirmationSettings,
  leadTime: LeadTimeSettings,
): OrderConfirmationSettings {
  return {
    ...base,
    use_lead_time_when_calculating_arrival: leadTime.useLeadTimeWhenCalculatingArrival,
    lead_time_in_days: leadTime.leadTimeInDays,
  }
}

export function automationFromSettings(settings: OrderConfirmationSettings): AutomationSettings {
  return {
    fullyAutomaticValidation: settings.fully_automatic_validation,
  }
}

export function applyAutomationToSettings(
  base: OrderConfirmationSettings,
  automation: AutomationSettings,
): OrderConfirmationSettings {
  return {
    ...base,
    fully_automatic_validation: automation.fullyAutomaticValidation,
  }
}

export function erpSyncFromSettings(settings: OrderConfirmationSettings): ErpSyncSettings {
  return {
    updateSupplierOrderNumberOnErpSync: settings.update_supplier_order_number_on_erp_sync,
  }
}

export function applyErpSyncToSettings(
  base: OrderConfirmationSettings,
  erpSync: ErpSyncSettings,
): OrderConfirmationSettings {
  return {
    ...base,
    update_supplier_order_number_on_erp_sync: erpSync.updateSupplierOrderNumberOnErpSync,
  }
}

export function flaggedLineFieldsFromSettings(
  settings: OrderConfirmationSettings,
): Set<OcLineComparisonFlagFieldId> {
  return new Set(
    OC_LINE_COMPARISON_FIELDS.filter((field) => settings[field.id]).map((field) => field.id),
  )
}

export function applyLineFlagsToSettings(
  base: OrderConfirmationSettings,
  flaggedLineFields: Set<OcLineComparisonFlagFieldId>,
): OrderConfirmationSettings {
  const next = { ...base }
  for (const field of OC_LINE_COMPARISON_FIELDS) {
    next[field.id] = flaggedLineFields.has(field.id)
  }
  return next
}

export function flaggedFieldsFromSettings(
  settings: OrderConfirmationSettings,
): Set<OcHeadComparisonFieldId> {
  return new Set(
    OC_HEAD_COMPARISON_FIELDS.filter((field) => settings[toFlagFieldId(field.id)]).map(
      (field) => field.id,
    ),
  )
}

export function applyThresholdsAndFlagsToSettings(
  base: OrderConfirmationSettings,
  thresholds: DeviationThresholds,
  flaggedFields: Set<OcHeadComparisonFieldId>,
): OrderConfirmationSettings {
  const next: OrderConfirmationSettings = {
    ...base,
    days_of_delay_deviation_threshold: thresholds.delayDays,
    days_of_early_deviation_threshold: thresholds.earlyDays,
    relative_price_difference_deviation_threshold: thresholds.priceDifferencePercent,
  }

  for (const field of OC_HEAD_COMPARISON_FIELDS) {
    next[toFlagFieldId(field.id)] = flaggedFields.has(field.id)
  }

  return next
}

export function toUpdateRequest(
  settings: OrderConfirmationSettings,
): UpdateOrderConfirmationSettingsRequest {
  const { id: _id, ...body } = settings
  return body
}

export function settingsEqual(a: OrderConfirmationSettings, b: OrderConfirmationSettings): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}
